import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async () => {
    window.dispatchEvent(new Event('open-auth'));
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      // Ignore user_not_found errors during signout
      if (error instanceof Error && !error.message.includes('user_not_found')) {
        throw error;
      }
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) throw new Error('Must be logged in to update password');

    try {
      // First verify current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: currentPassword
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;
    } catch (error) {
      throw error;
    }
  };

  const deleteAccount = async (password: string) => {
    if (!user) throw new Error('Must be logged in to delete account');

    try {
      // First verify password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: password
      });

      if (signInError) {
        throw new Error('Password is incorrect');
      }

      // Delete user data using RPC function
      const { error: deleteError } = await supabase.rpc('delete_user_account');

      if (deleteError) throw deleteError;

      // Sign out and redirect
      await supabase.auth.signOut({ scope: 'global' }); // Sign out from all devices
      window.location.href = '/'; // Force full page reload
    } catch (error) {
      throw error;
    }
  };

  return { user, isLoading, signIn, signOut, updatePassword, deleteAccount };
}